import { useEffect, useState } from "react";
import { spendWaitingOnFees } from "../lib/data";

/**
 * Live countdown to the next UTC-aligned payout attempt.
 * Cadence comes from worker snapshot (`schedule.payoutIntervalMin`, default 10).
 */
export function nextPayoutMs(intervalMin = 10, now = Date.now()) {
  const ms = Math.max(1, Number(intervalMin) || 10) * 60_000;
  return Math.floor(now / ms) * ms + ms;
}

export function formatCountdown(remainingMs) {
  const total = Math.max(0, Math.floor(remainingMs / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function NextPayoutCountdown({
  schedule,
  spend = null,
  compact = false,
  className = "",
}) {
  const intervalMin = Number(schedule?.payoutIntervalMin) || 10;
  const minSol = schedule?.minDistributeSol ?? 0.02;
  const waiting = spendWaitingOnFees(spend);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const target = nextPayoutMs(intervalMin, now);
  const remaining = target - now;
  const clock = formatCountdown(remaining);
  const targetIso = new Date(target).toISOString();

  if (compact) {
    return (
      <span
        className={`payout-cd payout-cd--compact ${waiting ? "payout-cd--waiting" : ""} ${className}`.trim()}
        title={
          waiting
            ? `Next attempt ${targetIso} · waiting on ≥ ${minSol} SOL fees`
            : `Next attempt ${targetIso}`
        }
      >
        <span className="payout-cd__label">{waiting ? "Next attempt" : "Next payout"}</span>
        <strong className="payout-cd__clock mono">{clock}</strong>
      </span>
    );
  }

  return (
    <div
      className={`payout-cd ${waiting ? "payout-cd--waiting" : ""} ${className}`.trim()}
      aria-live="polite"
    >
      <div className="payout-cd__copy">
        <p className="section-kicker" style={{ marginBottom: "0.35rem" }}>
          <i className="brand-diamond" aria-hidden />{" "}
          {waiting ? "Next payout attempt" : "Next payout"}
        </p>
        <strong className="payout-cd__clock mono">{clock}</strong>
        <p className="payout-cd__note">
          {waiting
            ? `Every ${intervalMin}m UTC · last window skipped — need ≥ ${minSol} SOL liquid fees`
            : `Every ${intervalMin} minutes (UTC)${
                minSol != null ? ` · runs when liquid ≥ ${minSol} SOL` : ""
              }`}
        </p>
      </div>
      <div className="payout-cd__meta">
        <span>{waiting ? "Retry" : "Attempt"}</span>
        <time dateTime={targetIso}>
          {new Date(target).toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZoneName: "short",
          })}
        </time>
      </div>
    </div>
  );
}
